import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { Lang } from "@/content/site";

const STORAGE_KEY = "msy_cookie_consent";

const COPY = {
  en: {
    tag: "Cookie Policy",
    body:
      'We use cookies to enhance your browsing experience, analyze site traffic and personalize content. By clicking "Accept", you consent to our use of cookies. You can change your preferences at any time using the button on the bottom-left.',
    accept: "Accept",
    reject: "Reject",
    learn: "Learn more",
    manage: "Cookie preferences",
    policyHref: "/cookie-policy",
  },
  pt: {
    tag: "Política de Cookies",
    body:
      'Utilizamos cookies para melhorar a sua experiência de navegação, analisar o tráfego do site e personalizar conteúdos. Ao clicar em "Aceitar", consente a utilização de cookies. Pode alterar as suas preferências a qualquer momento através do botão no canto inferior esquerdo.',
    accept: "Aceitar",
    reject: "Rejeitar",
    learn: "Saber mais",
    manage: "Preferências de cookies",
    policyHref: "/pt/politica-de-cookies",
  },
} as const;

export default function CookieConsent({ lang }: { lang: Lang }) {
  const copy = COPY[lang];
  const [decided, setDecided] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) {
        setDecided(false);
        setOpen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const save = (value: "accepted" | "rejected") => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore
    }
    try {
      const w = window as unknown as {
        gtag?: (...args: unknown[]) => void;
        __msyLoadTracking?: () => void;
      };
      if (typeof w.gtag === "function") {
        const state = value === "accepted" ? "granted" : "denied";
        w.gtag("consent", "update", {
          analytics_storage: state,
          ad_storage: state,
          ad_user_data: state,
          ad_personalization: state,
        });
      }
      // Fire the deferred GTM loader immediately on accept (don't wait for 3s/interaction)
      if (value === "accepted" && typeof w.__msyLoadTracking === "function") {
        w.__msyLoadTracking();
      }
    } catch {
      // ignore
    }
    setDecided(true);
    setOpen(false);
  };

  return (
    <>
      {/* Floating settings icon (bottom-left) */}
      {decided && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={copy.manage}
          title={copy.manage}
          className="fixed bottom-5 left-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-gold text-emerald-deep shadow-xl shadow-black/30 ring-2 ring-white/70 transition-transform hover:scale-105 hover:bg-gold/90"
        >
          <Cookie className="h-6 w-6" aria-hidden="true" strokeWidth={2.25} />
        </button>
      )}

      {/* Banner */}
      {open && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label={copy.tag}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-lg border border-gold/40 bg-white text-emerald-deep shadow-2xl sm:inset-x-auto sm:left-1/2 sm:bottom-6 sm:-translate-x-1/2"
        >
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <Cookie className="h-4 w-4 text-gold" aria-hidden="true" />
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
                {copy.tag}
              </p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-emerald-deep/85">
              {copy.body}{" "}
              <Link to={copy.policyHref} className="underline hover:text-gold">
                {copy.learn}
              </Link>
              .
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => save("rejected")}
                className="inline-flex items-center justify-center rounded border border-emerald-deep/30 px-5 py-2 text-xs uppercase tracking-[0.18em] text-emerald-deep transition-colors hover:bg-emerald-deep/5"
              >
                {copy.reject}
              </button>
              <button
                type="button"
                onClick={() => save("accepted")}
                className="inline-flex items-center justify-center rounded bg-gold px-5 py-2 text-xs uppercase tracking-[0.18em] text-emerald-deep font-semibold transition-colors hover:bg-gold/90"
              >
                {copy.accept}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}